import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

/*
  VehicleTagView.jsx
  ─────────────────────────────────────────────────────────────────────────────
  Public page shown when someone scans any vehicle NFC tag.
  Works for: cars, bikes, trucks, buses, scooters — any vehicle.

  Changes from previous version:
  • All "car" text replaced with "vehicle" throughout
  • Reason texts updated to say "vehicle" not "car"
  • After ANY interaction (reason selected + action taken), the owner is alerted
    via POST /api/flapfindmy/:flapid/vehicle-alert — stores the alert in DB
    AND triggers a WhatsApp/SMS notification to the registered phone
  • Alert confirmation shown to the person who tapped ("Owner notified ✓")
  • Theme updated to match #1c73ba brand blue (consistent with rest of app)
  • Improved success state with clear confirmation
  • "Something wrong" reason renamed + all icons refreshed

  Route: /v/:flapid  (add in App.js BEFORE /:flapid wildcard)
───────────────────────────────────────────────────────────────────────────── */

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/$/, '');

const BLUE = '#1c73ba';
const BL   = '#e8f3fb';
const FONT = "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif";

// ── Reasons — all say "vehicle" not "car" ────────────────────────────────────
const REASONS = [
  { id:'lights',   icon:'💡', text:'The lights of this vehicle are on.'          },
  { id:'parking',  icon:'🅿️', text:'This vehicle is in a no-parking zone.'      },
  { id:'towed',    icon:'🚛', text:'This vehicle is being towed.'                },
  { id:'window',   icon:'🪟', text:'A window or door of this vehicle is open.'  },
  { id:'accident', icon:'⚠️', text:'This vehicle is blocking traffic / accident.'},
  { id:'other',    icon:'🔔', text:'Something else is wrong with this vehicle.'  },
];

// ── Nepal flag ────────────────────────────────────────────────────────────────
function NepalFlag({ size = 30 }) {
  return (
    <svg width={size} height={size * 1.22} viewBox="0 0 40 49" xmlns="http://www.w3.org/2000/svg">
      {/* Blue outline */}
      <polygon points="0,0 40,0 40,5 18,5 18,22 40,22 40,27 0,49" fill="#003893"/>
      {/* Red body */}
      <polygon points="2,2 36,2 36,4 16,4 16,20 36,20 36,25 2,47" fill="#DC143C"/>
      <polygon points="2,2 16,2 16,20 2,47" fill="#DC143C"/>
      {/* Moon (simplified) */}
      <circle cx="10" cy="29" r="3.5" fill="white" opacity="0.92"/>
      {/* Sun (simplified) */}
      <circle cx="10" cy="14" r="4.5" fill="white" opacity="0.92"/>
    </svg>
  );
}

// ── Nepal-style license plate ────────────────────────────────────────────────
function LicensePlate({ plate }) {
  if (!plate) return null;
  return (
    <div style={{
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'center',
      background: '#fff',
      border: '3px solid #111',
      borderRadius: 8,
      padding: '8px 28px 10px',
      boxShadow: '0 4px 0 #bbb, 0 6px 16px rgba(0,0,0,0.13)',
      minWidth: 190,
      position: 'relative',
    }}>
      {/* Top blue strip like Nepal govt plates */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 6,
        background: BLUE, borderRadius: '5px 5px 0 0',
      }}/>
      <div style={{
        marginTop: 8,
        fontSize: 28,
        fontWeight: 900,
        fontFamily: "'Courier New', monospace",
        color: '#111',
        letterSpacing: 4,
        lineHeight: 1.2,
        textAlign: 'center',
      }}>
        {plate.toUpperCase()}
      </div>
    </div>
  );
}

// ── Spinner ───────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{
      width: 32, height: 32, margin: '60px auto',
      borderRadius: '50%',
      border: `3px solid ${BL}`,
      borderTopColor: BLUE,
      animation: 'v-spin 0.7s linear infinite',
    }}/>
  );
}

// ── Alert banner shown to the tapper after sending ────────────────────────────
function OwnerAlerted({ plate, reason, onDone }) {
  return (
    <div style={{ textAlign: 'center', padding: '10px 0 6px' }}>
      <div style={{
        width: 64, height: 64, margin: '0 auto 16px',
        borderRadius: '50%',
        background: 'rgba(16,185,129,0.1)',
        border: '2px solid rgba(16,185,129,0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 30,
      }}>✅</div>
      <div style={{
        fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 8,
        fontFamily: FONT,
      }}>Owner notified!</div>
      <div style={{
        fontSize: 13, color: '#6b7280', lineHeight: 1.7,
        marginBottom: 20, maxWidth: 300, margin: '0 auto 20px',
      }}>
        {plate && <>The owner of <strong>{plate}</strong> has been alerted.</>}
        {!plate && <>The vehicle owner has been alerted.</>}
        {reason && <><br/><span style={{ color: BLUE, fontWeight: 600 }}>{reason}</span></>}
      </div>
      {/* Green success banner */}
      <div style={{
        padding: '12px 16px',
        background: 'rgba(16,185,129,0.08)',
        border: '1px solid rgba(16,185,129,0.2)',
        borderRadius: 12, fontSize: 13, color: '#065f46', marginBottom: 16,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span>📱</span>
        <span>A notification has been sent to the owner's registered phone number.</span>
      </div>
      <button onClick={onDone}
        style={{
          width: '100%', padding: '13px', borderRadius: 12,
          border: `1.5px solid ${BLUE}`, background: BL,
          color: BLUE, fontSize: 14, fontWeight: 700,
          cursor: 'pointer', fontFamily: FONT,
        }}>
        Done
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────────────────────
export default function VehicleTagView() {
  const { flapid: rawFlapid } = useParams();
  const navigate = useNavigate();
  const flapid = (rawFlapid || '').replace(/^:+/, '').trim();

  const [pageState,  setPageState]  = useState('loading'); // loading | found | error
  const [data,       setData]       = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  // Alert flow states
  const [alertState, setAlertState] = useState('idle'); // idle | alerting | alerted | error
  const [alertError, setAlertError] = useState('');

  // Contact form (shown when no phone available)
  const [showForm,     setShowForm]     = useState(false);
  const [contactForm,  setContactForm]  = useState({ name: '', phone: '' });
  const [formBusy,     setFormBusy]     = useState(false);
  const [formSent,     setFormSent]     = useState(false);

  // ── Fetch tag data ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!flapid) { setPageState('error'); return; }
    axios.get(`${API_BASE}/userinfo/${encodeURIComponent(flapid)}`)
      .then(({ data: res }) => {
        if (res.redirect) { window.location.href = res.redirect; return; }
        const user = res.user || res;
        const ctx  = user?.accessoryContext;
        if (ctx?.type && ctx.type !== 'vehicle') {
          navigate(`/${flapid}`, { replace: true });
          return;
        }
        setData({ user, ctx, flapid, isLost: !!res.isLost });
        setPageState('found');
      })
      .catch(() => setPageState('error'));
  }, [flapid, navigate]);

  // ── Alert owner — called whenever someone selects a reason and sends ────────
  // Hits POST /api/flapfindmy/:flapid/vehicle-alert which:
  //   1. Saves the alert in accessories.$.tagMeta.vehicleAlerts[]
  //   2. Sends a WhatsApp message to the owner's registered phone
  //   3. If WhatsApp fails, falls back to SMS via the configured SMS gateway
  // Also calls the existing /finder endpoint to appear in the Dashboard alerts panel.
  const sendAlert = useCallback(async (reason, contactName = '', contactPhone = '') => {
    if (!selectedId && !reason) return;
    const chosen = reason || REASONS.find(r => r.id === selectedId);
    if (!chosen) return;

    setAlertState('alerting');
    setAlertError('');

    const payload = {
      reason:       chosen.text,
      reasonId:     chosen.id,
      reporterName: contactName  || 'Anonymous',
      reporterPhone:contactPhone || '',
    };

    try {
      // Primary: vehicle-alert endpoint (saves + notifies owner)
      await axios.post(`${API_BASE}/flapfindmy/${flapid}/vehicle-alert`, payload);
    } catch {
      // Fallback: generic finder endpoint (saves in dashboard alerts)
      try {
        await axios.post(`${API_BASE}/flapfindmy/${flapid}/finder`, {
          name:    contactName || 'Anonymous',
          phone:   contactPhone || '',
          message: chosen.text,
          reason:  chosen.text,
        });
      } catch (err2) {
        setAlertState('error');
        setAlertError('Could not send alert. Please try calling directly.');
        return;
      }
    }
    setAlertState('alerted');
  }, [flapid, selectedId]);

  // ── WhatsApp handler ───────────────────────────────────────────────────────
  const handleWhatsApp = useCallback(() => {
    const phone = data?.ctx?.tagMeta?.ownerContact || data?.user?.phone_number_1 || '';
    const plate = data?.ctx?.tagMeta?.plate || '';
    const reason = REASONS.find(r => r.id === selectedId);
    const text = reason
      ? `Hi, I'm contacting you about your vehicle ${plate}. ${reason.text}`
      : `Hi, I wanted to let you know about your vehicle${plate ? ' ' + plate : ''}.`;

    // First record the alert in the backend
    sendAlert(reason);

    // Then open WhatsApp (with or without a number)
    const clean = phone.replace(/\D/g, '');
    if (clean) {
      window.open(`https://wa.me/${clean}?text=${encodeURIComponent(text)}`, '_blank');
    } else {
      setShowForm(true);
    }
  }, [data, selectedId, sendAlert]);

  // ── Direct call ────────────────────────────────────────────────────────────
  const handleCall = useCallback(() => {
    const phone = data?.ctx?.tagMeta?.ownerContact || data?.user?.phone_number_1 || '';
    if (phone) {
      sendAlert(null); // record alert before calling
      window.location.href = `tel:${phone}`;
    } else {
      setShowForm(true);
    }
  }, [data, sendAlert]);

  // ── Contact form submit (when no phone available) ──────────────────────────
  const submitForm = useCallback(async () => {
    if (!contactForm.name.trim()) return;
    setFormBusy(true);
    const reason = REASONS.find(r => r.id === selectedId);
    try {
      await axios.post(`${API_BASE}/flapfindmy/${flapid}/vehicle-alert`, {
        reason:       reason?.text || 'Vehicle alert',
        reasonId:     reason?.id   || 'other',
        reporterName: contactForm.name,
        reporterPhone:contactForm.phone,
      });
      setFormSent(true);
    } catch {
      try {
        await axios.post(`${API_BASE}/flapfindmy/${flapid}/finder`, {
          name:    contactForm.name,
          phone:   contactForm.phone,
          message: reason?.text || 'Vehicle alert',
          reason:  reason?.text || '',
        });
        setFormSent(true);
      } catch { setAlertError('Failed to send. Try again.'); }
    }
    setFormBusy(false);
  }, [contactForm, flapid, selectedId]);

  const handleEmergency = () => window.open('tel:100');

  // ── Render ─────────────────────────────────────────────────────────────────
  if (pageState === 'loading') return (
    <div style={S.root}>
      <style>{`@keyframes v-spin{to{transform:rotate(360deg)}}`}</style>
      <Spinner/>
    </div>
  );

  if (pageState === 'error') return (
    <div style={S.root}>
      <style>{`@keyframes v-spin{to{transform:rotate(360deg)}}`}</style>
      <div style={S.banner}><NepalFlag/><span style={S.bannerText}>NEPAL</span></div>
      <div style={S.card}>
        <div style={{ textAlign: 'center', padding: '28px 0 8px' }}>
          <div style={{ fontSize: 52, marginBottom: 14 }}>🚗</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
            Vehicle Tag Not Found
          </div>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 22, lineHeight: 1.7 }}>
            This NFC tag isn't registered to a Flap vehicle yet.<br/>
            Contact your tag provider for help.
          </div>
          <a href="https://flapcard.com.np" style={{ ...S.btnOutline, display: 'inline-flex' }}>
            Learn about Flap Vehicle Tags →
          </a>
        </div>
      </div>
    </div>
  );

  const user     = data?.user || {};
  const ctx      = data?.ctx  || {};
  const tagMeta  = ctx.tagMeta || {};
  const plate    = tagMeta.plate || '';
  const vehicle  = tagMeta.vehicle || '';
  const ownerPhone = tagMeta.ownerContact || user.phone_number_1 || '';
  const selectedReason = REASONS.find(r => r.id === selectedId);

  return (
    <div style={S.root}>
      <style>{`
        @keyframes v-spin { to { transform: rotate(360deg); } }
        @keyframes v-fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .vbtn:hover { opacity: 0.88; }
        .vreason:hover { background: ${BL}; border-color: ${BLUE}; }
        .vreason.sel { background: ${BL}; border-color: ${BLUE}; }
      `}</style>

      {/* ── Nepal header banner ── */}
      {/* <div style={S.banner}>
        <NepalFlag size={32}/>
        <span style={S.bannerText}>NEPAL</span>
        <div style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
          fontSize: 11, color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
          Powered by Flap
        </div>
      </div> */}

      {/* ── Main card ── */}
      <div style={S.card}>

        {/* Heading */}
        <div style={{ marginBottom: 16 }}>
          <h1 style={S.heading}>Contact vehicle owner</h1>
          {vehicle && (
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3 }}>{vehicle}</div>
          )}
        </div>

        {/* Plate */}
        {plate && (
          <div style={{ textAlign: 'center', margin: '0 0 22px' }}>
            <LicensePlate plate={plate}/>
          </div>
        )}

        {/* ── SUCCESS: owner alerted ── */}
        {alertState === 'alerted' ? (
          <OwnerAlerted
            plate={plate}
            reason={selectedReason?.text}
            onDone={() => { setAlertState('idle'); setSelectedId(null); setShowForm(false); setFormSent(false); }}
          />

        ) : formSent ? (
          <OwnerAlerted plate={plate} reason={selectedReason?.text}
            onDone={() => { setShowForm(false); setFormSent(false); }}/>

        ) : showForm ? (
          /* ── Contact form — when owner has no registered phone ── */
          <div style={{ animation: 'v-fadeIn .25s ease' }}>
            <div style={{ fontSize: 14, color: '#374151', marginBottom: 16, lineHeight: 1.6 }}>
              Leave your contact so the vehicle owner can reach you.
            </div>
            {[
              { label: 'Your name *', key: 'name', type: 'text',  ph: 'Full name' },
              { label: 'Your phone',  key: 'phone', type: 'tel',  ph: '+977 98XXXXXXXX' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 14 }}>
                <label style={S.label}>{f.label}</label>
                <input type={f.type} placeholder={f.ph}
                  value={contactForm[f.key]}
                  onChange={e => setContactForm(p => ({ ...p, [f.key]: e.target.value }))}
                  style={S.input}/>
              </div>
            ))}
            {alertError && (
              <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 10 }}>{alertError}</div>
            )}
            <button className="vbtn" onClick={submitForm}
              disabled={formBusy || !contactForm.name.trim()}
              style={{ ...S.btnBlue, opacity: (formBusy || !contactForm.name.trim()) ? 0.6 : 1 }}>
              {formBusy ? '⏳ Sending…' : '📨 Send message to owner'}
            </button>
            <button className="vbtn" onClick={() => setShowForm(false)} style={{ ...S.btnOutline, marginTop: 10 }}>
              ← Back
            </button>
          </div>

        ) : (
          /* ── Main flow: reason picker + action buttons ── */
          <div style={{ animation: 'v-fadeIn .2s ease' }}>

            {/* Reason label */}
            <div style={S.reasonLabel}>Why contact the vehicle owner?</div>

            {/* Reason buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 22 }}>
              {REASONS.map(r => (
                <button key={r.id}
                  className={`vreason${selectedId === r.id ? ' sel' : ''}`}
                  onClick={() => setSelectedId(id => id === r.id ? null : r.id)}
                  style={{
                    ...S.reasonBtn,
                    background: selectedId === r.id ? BL : '#fff',
                    borderColor: selectedId === r.id ? BLUE : '#e5e7eb',
                  }}>
                  <span style={{ fontSize: 21, lineHeight: 1, flexShrink: 0 }}>{r.icon}</span>
                  <span style={{
                    flex: 1, textAlign: 'left', fontSize: 14, color: '#1a1a1a',
                    fontWeight: selectedId === r.id ? 600 : 400, lineHeight: 1.4,
                  }}>
                    {r.text}
                  </span>
                  {/* Radio circle */}
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                    border: `2px solid ${selectedId === r.id ? BLUE : '#d1d5db'}`,
                    background: selectedId === r.id ? BLUE : '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all .15s',
                  }}>
                    {selectedId === r.id && (
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }}/>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Alerting indicator */}
            {alertState === 'alerting' && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px', borderRadius: 10, marginBottom: 12,
                background: BL, border: `1px solid ${BLUE}22`, fontSize: 13, color: BLUE,
              }}>
                <div style={{
                  width: 16, height: 16, borderRadius: '50%',
                  border: `2px solid ${BL}`,
                  borderTopColor: BLUE,
                  animation: 'v-spin .6s linear infinite', flexShrink: 0,
                }}/>
                Notifying the owner…
              </div>
            )}

            {/* Alert error */}
            {alertState === 'error' && (
              <div style={{
                padding: '10px 14px', borderRadius: 10, marginBottom: 12,
                background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)',
                fontSize: 13, color: '#ef4444',
              }}>
                ⚠️ {alertError}
              </div>
            )}

            {/* Selected reason preview */}
            {selectedReason && (
              <div style={{
                padding: '10px 14px', borderRadius: 10, marginBottom: 12,
                background: BL, border: `1px solid ${BLUE}22`,
                fontSize: 13, color: BLUE, fontWeight: 500,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span>{selectedReason.icon}</span>
                <span>Selected: <strong>{selectedReason.text}</strong></span>
              </div>
            )}

            {/* Action buttons */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
              {/* WhatsApp Message */}
              <button className="vbtn" onClick={handleWhatsApp}
                disabled={alertState === 'alerting'}
                style={S.btnDark}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="#25D366" style={{ flexShrink: 0 }}>
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Message
              </button>

              {/* Private Call */}
              <button className="vbtn" onClick={handleCall}
                disabled={alertState === 'alerting'}
                style={S.btnBlue}>
                <span>📞</span> Private call
              </button>
            </div>

            {/* Alert only (no contact — just notify) */}
            {selectedId && alertState === 'idle' && (
              <button className="vbtn" onClick={() => sendAlert(selectedReason)} style={S.btnOutline}>
                🔔 Just notify the owner (no contact needed)
              </button>
            )}

            {/* Spam notice */}
            <p style={S.spamNotice}>
              Please note: spam may get your IP and number blocked on the platform for up to 6 months.
            </p>
          </div>
        )}

        {/* Flap footer */}
        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 11, color: '#d1d5db' }}>
          Powered by{' '}
          <a href="https://flapcard.com.np"
            style={{ color: BLUE, fontWeight: 600, textDecoration: 'none' }}>Flap</a>
          {' '}vehicle tag
        </div>
      </div>

      {/* ── Emergency button — always visible bottom right ── */}
      <button className="vbtn" onClick={handleEmergency} style={S.emergencyBtn}>
        <span>🚑</span>
        <span>Emergency</span>
      </button>
    </div>
  );
}

/* ── Styles ─────────────────────────────────────────────────────────────────── */
// const BLUE = '#1c73ba';
//const BL   = '#e8f3fb';
//const FONT = "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif";

const S = {
  root: {
    minHeight: '100dvh',
    background: '#f3f4f6',
    fontFamily: FONT,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0 0 120px',
    position: 'relative',
    color: '#111827',
    WebkitFontSmoothing: 'antialiased',
  },
  banner: {
    width: '100%',
    background: `linear-gradient(135deg,#1555a0,${BLUE})`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: '12px 20px',
    position: 'sticky',
    top: 0,
    zIndex: 50,
    boxShadow: '0 2px 12px rgba(28,115,186,0.25)',
  },
  bannerText: {
    color: '#fff',
    fontSize: 17,
    fontWeight: 800,
    letterSpacing: 3.5,
    textTransform: 'uppercase',
    fontFamily: FONT,
  },
  card: {
    background: '#fff',
    borderRadius: 20,
    padding: '22px 20px 20px',
    width: 'calc(100% - 32px)',
    maxWidth: 480,
    marginTop: 18,
    boxShadow: '0 2px 20px rgba(0,0,0,0.08)',
    border: '1px solid #e5e7eb',
  },
  heading: {
    fontSize: 21,
    fontWeight: 700,
    color: '#111827',
    margin: 0,
    fontFamily: FONT,
  },
  reasonLabel: {
    fontSize: 13,
    color: '#6b7280',
    marginBottom: 10,
    fontWeight: 500,
  },
  reasonBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '13px 14px',
    borderRadius: 12,
    border: '1.5px solid #e5e7eb',
    cursor: 'pointer',
    transition: 'all .15s',
    background: '#fff',
    width: '100%',
    fontFamily: FONT,
    textAlign: 'left',
  },
  btnDark: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '13px 12px',
    borderRadius: 12,
    border: 'none',
    background: '#111827',
    color: '#f5a623',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: FONT,
    width: '100%',
    transition: 'opacity .15s',
  },
  btnBlue: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '13px 12px',
    borderRadius: 12,
    border: 'none',
    background: BLUE,
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: FONT,
    width: '100%',
    transition: 'opacity .15s',
    textDecoration: 'none',
  },
  btnOutline: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '11px 14px',
    borderRadius: 12,
    border: `1.5px solid ${BLUE}`,
    background: BL,
    color: BLUE,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: FONT,
    width: '100%',
    transition: 'opacity .15s',
    textDecoration: 'none',
  },
  label: {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 5,
    fontFamily: FONT,
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    border: '1.5px solid #e5e7eb',
    borderRadius: 10,
    fontSize: 15,
    outline: 'none',
    fontFamily: FONT,
    boxSizing: 'border-box',
    color: '#111827',
    transition: 'border .15s',
  },
  spamNotice: {
    fontSize: 12,
    color: '#9ca3af',
    lineHeight: 1.6,
    marginTop: 14,
    marginBottom: 0,
  },
  emergencyBtn: {
    position: 'fixed',
    bottom: 22,
    right: 18,
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    padding: '13px 20px',
    borderRadius: 99,
    border: 'none',
    background: '#ef4444',
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 4px 20px rgba(239,68,68,0.38)',
    fontFamily: FONT,
    zIndex: 100,
    transition: 'opacity .15s',
  },
};